import {CheckIcon, CopyIcon} from '@primer/octicons-react'
import {Button, Text} from '@primer/react-brand/esm'
import {clsx} from 'clsx'
import {useEffect, useId, useRef, useState, type MouseEventHandler} from 'react'

import {announce} from '@github-ui/aria-live'
import {getAnalyticsEvent} from '@github-ui/swp-core/lib/utils/analytics'

import Styles from './MobileCta.module.css'

const COPIED_STATE_DURATION_MS = 2000

export type MobileCtaLocation = 'hero' | 'highlight' | 'cta_banner'
export type CopyToClipboard = (text: string) => Promise<void>
export type NativeShare = (data: ShareData) => Promise<void>

export type MobileNativeShareCopy = {
  cancelledMessage: string
  copiedMessage: string
  copiedTriggerLabel: string
  copyErrorMessage: string
  copyTriggerLabel: string
  copyUnsupportedMessage: string
  errorMessage: string
  triggerLabel: string
  unsupportedMessage: string
}

export type MobileCtaConfig =
  | {
      kind: 'emailHandoff'
      label: string
      onClick: MouseEventHandler<HTMLButtonElement>
    }
  | {
      kind: 'nativeShare'
      clipboardWrite?: CopyToClipboard | null
      copy: MobileNativeShareCopy
      share?: NativeShare | null
    }

type MobileCtaProps = {
  block?: boolean
  className?: string
  config: MobileCtaConfig
  location: MobileCtaLocation
}

type ShareMessage = {
  text: string
  type: 'error' | 'status'
}

function isShareCancellation(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

export function MobileCta({block, className, config, location}: MobileCtaProps) {
  const messageId = useId()
  const actionInProgressRef = useRef(false)
  const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [isCopied, setIsCopied] = useState(false)
  const [mode, setMode] = useState<'copy' | 'share'>('share')
  const [shareMessage, setShareMessage] = useState<ShareMessage | null>(null)

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current !== undefined) clearTimeout(copiedResetTimeoutRef.current)
    }
  }, [])

  if (config.kind === 'emailHandoff') {
    return (
      <Button
        aria-haspopup="dialog"
        block={block}
        className={className}
        type="button"
        variant="primary"
        {...getAnalyticsEvent({
          action: config.label,
          tag: 'button',
          context: 'mobile_handoff',
          location,
        })}
        onClick={config.onClick}
      >
        {config.label}
      </Button>
    )
  }

  const {clipboardWrite, copy, share: configuredShare} = config

  function showShareMessage(message: ShareMessage) {
    setShareMessage(message)
    announce(message.text, {assertive: message.type === 'error'})
  }

  function showCopiedState() {
    setIsCopied(true)
    if (copiedResetTimeoutRef.current !== undefined) clearTimeout(copiedResetTimeoutRef.current)

    copiedResetTimeoutRef.current = setTimeout(() => {
      setIsCopied(false)
      copiedResetTimeoutRef.current = undefined
    }, COPIED_STATE_DURATION_MS)
  }

  async function shareCurrentPage() {
    if (actionInProgressRef.current) return

    const share =
      configuredShare === undefined
        ? typeof navigator === 'undefined' || typeof navigator.share !== 'function'
          ? null
          : (data: ShareData) => navigator.share(data)
        : configuredShare

    if (!share) {
      setMode('copy')
      showShareMessage({text: copy.unsupportedMessage, type: 'error'})
      return
    }

    actionInProgressRef.current = true
    setShareMessage(null)

    try {
      await share({url: window.location.href})
    } catch (error) {
      const wasCancelled = isShareCancellation(error)

      if (!wasCancelled) setMode('copy')

      showShareMessage({
        text: wasCancelled ? copy.cancelledMessage : copy.errorMessage,
        type: wasCancelled ? 'status' : 'error',
      })
    } finally {
      actionInProgressRef.current = false
    }
  }

  async function copyCurrentPage() {
    if (actionInProgressRef.current) return

    const copyToClipboard =
      clipboardWrite === undefined
        ? typeof navigator === 'undefined' || typeof navigator.clipboard?.writeText !== 'function'
          ? null
          : (text: string) => navigator.clipboard.writeText(text)
        : clipboardWrite

    if (!copyToClipboard) {
      showShareMessage({text: copy.copyUnsupportedMessage, type: 'error'})
      return
    }

    actionInProgressRef.current = true
    setShareMessage(null)

    try {
      await copyToClipboard(window.location.href)
      announce(copy.copiedMessage)
      showCopiedState()
    } catch {
      showShareMessage({text: copy.copyErrorMessage, type: 'error'})
    } finally {
      actionInProgressRef.current = false
    }
  }

  const isCopyMode = mode === 'copy'
  const triggerLabel = isCopyMode ? copy.copyTriggerLabel : copy.triggerLabel
  const visibleLabel = isCopied ? copy.copiedTriggerLabel : triggerLabel

  return (
    <div className={clsx(Styles.MobileCta, className)}>
      <Button
        aria-describedby={shareMessage ? messageId : undefined}
        block={block}
        leadingVisual={isCopyMode ? isCopied ? <CheckIcon /> : <CopyIcon /> : undefined}
        type="button"
        variant="primary"
        {...getAnalyticsEvent({
          action: triggerLabel,
          tag: 'button',
          context: isCopyMode ? 'mobile_native_share_fallback' : 'mobile_native_share',
          location,
        })}
        onClick={isCopyMode ? copyCurrentPage : shareCurrentPage}
      >
        {visibleLabel}
      </Button>

      {shareMessage ? (
        <Text as="p" className={Styles.MobileCta__message} id={messageId} size="100" variant="muted">
          {shareMessage.text}
        </Text>
      ) : null}
    </div>
  )
}
